import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TableauViz, Toolbar, TableauEventType } from '@tableau/embedding-api';
import { Snackbar, Alert } from '@mui/material';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import Loader from '../Loader'; 


const ReportPage: React.FC<{ isCollapsed: boolean }> = ({ isCollapsed })  => {
  const { reportId } = useParams<{ reportId?: string }>();
  const vizRef = useRef<HTMLDivElement>(null);
  const [token, setToken] = useState<string>('');
  const [reportName, setReportName] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const tableauToken = useSelector(
    (state: RootState) => state.auth.tableauToken,
  );
  const reportUrl = process.env.REACT_APP_DASHBOARD_REPORT_URL ?? '';

  useEffect(() => {
    if (tableauToken !== null) {
      const fetchData = async (): Promise<void> => {
        try {
          setToken(tableauToken);
        } catch (error) {
          console.error('Error generating token:', error);
          setIsLoading(false);
          setSnackbarMessage('Error loading report. Please try again.');
          setSnackbarOpen(true);
        }
      };

      void fetchData();
    }
  }, [tableauToken]);

  const formatReportId = (id: string): string => {
    return id
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  useEffect(() => {
    if (vizRef.current && token !== '') {
      setupTableauViz();
    }
  }, [token, reportId]);
  useEffect(() => {
    setIsLoading(true);
    setupTableauViz();
    const handleResize = (): void => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
      calculateHeight();
    };

    window.addEventListener('resize', handleResize);
    calculateHeight(); 

    return () => window.removeEventListener('resize', handleResize);
  }, [isCollapsed]);
  const calculateHeight = ():void => {
    if (vizRef.current) {
      const headerHeight = 100; 
      const footerHeight = 20; 
      const padding = 0; 
      
      let availableHeight;
      
      if (window.innerHeight >= 945) {
                
        vizRef.current.style.height = `75vh`;
        vizRef.current.style.width = 'auto';
      } else {
        // For other resolutions
        availableHeight = window.innerHeight - headerHeight - footerHeight - padding;
        vizRef.current.style.height = `${availableHeight}px`;
        vizRef.current.style.width = 'auto';
        vizRef.current.style.overflow = 'auto';
      }
    }
  };
  useEffect(() => {
    const handleResize = (): void => {
      calculateHeight(); // Recalculate height on window resize
    };
  
    window.addEventListener('resize', handleResize);
  
    calculateHeight(); // Calculate height on initial mount
  
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  useEffect(() => {
    setIsLoading(true);
    setupTableauViz();
    const handleResize = (): void => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
      calculateHeight();
    };

    window.addEventListener('resize', handleResize);
    calculateHeight(); // Initial height calculation on mount

    return () => window.removeEventListener('resize', handleResize);
  }, [isCollapsed]);

  const setupTableauViz = (): void => {
    setIsLoading(true);

    if (vizRef.current && token !== '') {
      const viz = new TableauViz();
      const reportName = formatReportId(reportId as string);
      setReportName(reportName);

      viz.src = `${reportUrl}/${reportName.replace(/\s/g, '')}`;
      viz.hideTabs = true;
      viz.toolbar = Toolbar.Hidden;
      viz.token = token;

      viz.addEventListener(TableauEventType.FirstInteractive, () => {
        setIsLoading(false);
      });

      vizRef.current.innerHTML = ''; 
      vizRef.current.appendChild(viz);
      
      vizRef.current.style.height = 'auto';
    }
  };

  const handleSnackbarClose = (): void => {
    setSnackbarOpen(false);
  };
  
  return (
   
        <div className="relative"> {/* Full height for the layout */}
          <div className="flex justify-between items-center mb-[20px]">
            <div>
              <h1 className="text-2xl text-title font-semibold">
                {reportName.includes('_') ? reportName.split('_')[0] : reportName}
              </h1>
            </div>
          </div>

      <div
        className="bg-white rounded-lg overflow-hidden relative"
        style={{ 
          height:  windowDimensions.height >= 945 
            ? '75vh' 
            : 'auto',
          overflow: windowDimensions.height >= 945  
            ? 'hidden' 
            : 'auto',
            width:'100%'
        }}
      >
        {isLoading && (
          <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Loader />
          </div>
        )}
        <div 
          ref={vizRef} 
          className="dashboard-card" 
          style={{ 
            width: '100%',
            height: windowDimensions.height >= 945 ? '75vh': 'auto',
            margin: '0 auto'
          }}
        />
      </div>
        

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity="error"
              sx={{ width: '100%' }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </div>
      
  );
};

export default ReportPage;
